import { useMemo } from "react"
import { Box, HStack, Stack } from "@chakra-ui/react"
import { Text } from "@/components"
import {
  CapTableFunding,
  CapTableNote,
  CompanyFmv,
} from "@/types"

interface Props {
  fmvs: CompanyFmv[]
  capTableNote: CapTableNote | null
  capTableFunding: CapTableFunding[]
}

const truncateNumber = (value: number): string => {
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000_000) return `$${(value / 1_000_000_000_000).toFixed(1)}T`
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toLocaleString()}`
}

const formatPctChange = (current: number, previous: number) => {
  if (previous === 0) return null
  const pct = ((current - previous) / Math.abs(previous)) * 100
  return {
    text: `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`,
    color: pct > 0 ? "green.600" : pct < 0 ? "red.600" : "gray.600",
  }
}

export const CompanyInfoCards = ({ fmvs, capTableNote, capTableFunding }: Props) => {
  // 1. Most recent FMV and % change from previous different value
  const fmvData = useMemo(() => {
    if (!fmvs || fmvs.length === 0) return null

    const _fmvs = fmvs.filter((f) => {
      return f.fmvTypeId === 1 && f.value && f.date
    })

    const sorted = [..._fmvs].sort(
      (a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime()
    )

    const latest = sorted[0]
    if (!latest) return null
    const latestValue = parseFloat(latest.value!)
    if (isNaN(latestValue)) return null

    // Find the previous FMV with a different value
    let prevValue: number | null = null
    for (let i = 1; i < sorted.length; i++) {
      const val = parseFloat(sorted[i].value!)
      if (!isNaN(val) && val !== latestValue) {
        prevValue = val
        break
      }
    }

    return {
      value: latestValue,
      date: latest.date,
      name: latest.name,
      pctChange: prevValue !== null ? formatPctChange(latestValue, prevValue) : null,
    }
  }, [fmvs])

  // 2. Fully diluted valuation + latest funding round
  const valuationData = useMemo(() => {
    const fdv = capTableNote?.fullyDilutedValuation
    let fdvValue: number | null = null
    if (fdv) {
      const parsed = parseFloat(String(fdv).replace(/[,$]/g, ""))
      if (!isNaN(parsed)) fdvValue = parsed
    }

    // Latest funding round by issueDate descending
    let latestRound: CapTableFunding | null = null
    if (capTableFunding && capTableFunding.length > 0) {
      const sorted = [...capTableFunding].sort(
        (a, b) => new Date(b.issueDate || 0).getTime() - new Date(a.issueDate || 0).getTime()
      )
      latestRound = sorted[0]
    }

    return { fdvValue, latestRound }
  }, [capTableNote, capTableFunding])

  return (
    <HStack w="100%" gap={4}>
      {/* Card 1: Most Recent FMV */}
      <Box
        flex={1}
        p={5}
        border="1px"
        borderColor="gray.200"
        borderRadius={10}
        boxShadow="sm"
        bg="card.bg"
      >
        <Text fontSize="xs" color="text.muted" fontWeight="medium">
          Most Recent FMV
        </Text>
        {fmvData ? (
          <>
            <Text fontSize="4xl" fontWeight="bold" color="blue.600">
              {truncateNumber(fmvData.value)}
            </Text>
            {fmvData.pctChange && (
              <Text fontSize="sm" fontWeight="semibold" color={fmvData.pctChange.color}>
                {fmvData.pctChange.text} vs prior
              </Text>
            )}
            <Text fontSize="xs" color="text.subtle" mt={2}>
              {fmvData.name} &middot; {fmvData.date ? new Date(fmvData.date).toLocaleDateString() : "N/A"}
            </Text>
          </>
        ) : (
          <Text color="text.subtle" mt={1}>N/A</Text>
        )}
      </Box>

      {/* Card 2: Fully Diluted Valuation + Latest Round */}
      <Box
        flex={1}
        p={5}
        border="1px"
        borderColor="gray.200"
        borderRadius={10}
        boxShadow="sm"
        bg="card.bg"
      >
        <Text fontSize="xs" color="text.muted" fontWeight="medium">
          Fully Diluted Valuation
        </Text>
        {valuationData.fdvValue !== null ? (
          <Text fontSize="4xl" fontWeight="bold" color="blue.600">
            {truncateNumber((Number(capTableNote?.totalAuthShares) * Math.max(Number(capTableNote?.fmv), Number(capTableNote?.esoLrp))))} 
          </Text>
        ) : (
          <Text color="text.subtle" mt={1}>N/A</Text>
        )}

        {valuationData.latestRound && (
          <Stack gap={0} mt={3}>
            <Text fontSize="lg" color="text.muted">
              LRP: ${valuationData.latestRound.conversionPrice}
            </Text>
            <Text fontSize="sm" fontWeight="semibold">
              {valuationData.latestRound.label}
            </Text>
            <Text fontSize="xs" color="text.subtle">
              Raised: {valuationData.latestRound.issueDate ? new Date(valuationData.latestRound.issueDate).toLocaleDateString() : "N/A"}
            </Text>
          </Stack>
        )}
      </Box>
    </HStack>
  )
}
